import React, { useState, useCallback, useEffect} from 'react';
import { getUploadUrl } from '../config/endpoints';
import { getCloudProvider, isAzureProvider, isGCPProvider } from '../config/cloudProvider';
import { useAuth } from '../components/auth/AuthContext';
import { 
  Box, Paper, Typography, Button, Chip, Alert, Card,
  CardHeader, CardContent, Divider
} from '../components/MuiComponents';

const FileUploadTest = () => {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [providerConfig, setProviderConfig] = useState(null);

  // Updated log sanitization
  const sanitizeLogMessage = (message) => {
    // Remove email addresses
    message = message.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]');
    
    // Remove URLs with query parameters
    message = message.replace(/https?:\/\/[^\s<>]*[^<>.,\s]/g, (url) => {
      try {
        const urlObj = new URL(url);
        return `${urlObj.origin}${urlObj.pathname}`;
      } catch {
        return url.split('?')[0];
      }
    });

    // Remove any potential authorization headers or tokens
    message = message.replace(/Bearer\s+[^\s]+/g, 'Bearer [REDACTED]');
    message = message.replace(/Authorization:\s*[^\s]+/g, 'Authorization: [REDACTED]');
    message = message.replace(/(?<=[\?&])(token|key|signature|credential)=[^&\s]+/gi, '$1=[REDACTED]');
    
    return message;
  };

  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    const sanitizedMessage = sanitizeLogMessage(`${timestamp} ${message}`);
    setLogs(prev => [...prev, { 
      timestamp, 
      message: sanitizedMessage, 
      type 
    }]);
  }, []);

  const sanitizeUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      // Remove all query parameters and hash
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch {
      // If URL parsing fails, remove everything after ? or #
      return url.split(/[?#]/)[0];
    }
  };

  const sanitizeResponseData = (data) => {
    const sensitiveKeys = [
      'signed_url', 'file_url', 'sas_token', 'token', 'key',
      'signature', 'credential', 'password', 'secret', 'auth'
    ];
    
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (sensitiveKeys.some(k => key.toLowerCase().includes(k))) {
        // For URLs, keep the base URL but remove parameters
        if (typeof value === 'string' && value.startsWith('http')) {
          acc[key] = sanitizeUrl(value);
        } else {
          acc[key] = '[REDACTED]';
        }
      } else if (typeof value === 'object' && value !== null) {
        acc[key] = sanitizeResponseData(value);
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const createTestFile = () => {
    const content = `Test file created at ${new Date().toISOString()}`;
    return new File([content], 'test-upload.txt', { type: 'text/plain' });
  };

  useEffect(() => {
    const checkProviderConfig = () => {
      const currentProvider = getCloudProvider();
      const config = {
        provider: currentProvider,
        isAzure: isAzureProvider(),
        isGCP: isGCPProvider()
      };
      setProviderConfig(config);
      addLog(`Provider Configuration: ${JSON.stringify({
        provider: config.provider,
        type: config.isAzure ? 'Azure' : config.isGCP ? 'GCP' : 'Unknown'
      })}`);
    };
    
    checkProviderConfig();
  }, [addLog]);

  const getStatusColor = () => {
    switch (status) {
      case 'complete':
        return 'success';
      case 'error':
        return 'error';
      case 'running':
        return 'primary';
      default:
        return 'default';
    }
  };
  
  const runTest = async () => {
    setStatus('running');
    setError(null);
    setLogs([]);

    try {
      addLog('Starting upload test...');
      addLog(`Using cloud provider: ${getCloudProvider()}`);

      const testFile = createTestFile();
      addLog(`Created test file: ${testFile.name} (${testFile.size} bytes)`);

      const provider = getCloudProvider().toUpperCase();
      const uploadEndpoint = getUploadUrl(provider);
      addLog(`Using upload endpoint: ${sanitizeUrl(uploadEndpoint)}`);
      
      let signedUrlData;
      try {
        const uploadUrl = `${uploadEndpoint}${isAzureProvider() ? '&' : '?'}filename=${encodeURIComponent(testFile.name)}`;
        
        addLog(`Requesting upload URL...`);
        const signedUrlResponse = await fetch(uploadUrl);
      
        if (!signedUrlResponse?.ok) {
          throw new Error(`HTTP error! status: ${signedUrlResponse?.status}`);
        }
        
        signedUrlData = await signedUrlResponse.json();
        addLog('Received upload URL successfully');
        
        // Log only sanitized data
        const sanitizedData = sanitizeResponseData(signedUrlData);
        addLog(`Upload configuration prepared`);
        
      } catch (error) {
        addLog(`Request failed: ${error.message}`, 'error');
        throw error;
      }

      const uploadConfig = {
        method: 'PUT',
        body: testFile,
        headers: {
          'Content-Type': testFile.type,
          ...(isAzureProvider() && { 
            'x-ms-blob-type': 'BlockBlob',
            'x-ms-version': '2020-04-08'
          })
        }
      };

      addLog('Starting file upload...');
      
      const uploadResponse = await fetch(signedUrlData.signed_url, uploadConfig);
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      addLog('Upload completed successfully', 'success');

      if (isAzureProvider()) {
        addLog('Verifying upload...', 'info');
        await new Promise(resolve => setTimeout(resolve, 2000));
        addLog('Upload verified', 'success');
      } else {
        addLog('Upload verification not required', 'info');
      }

      const finalFileUrl = sanitizeUrl(signedUrlData.file_url || signedUrlData.signed_url);
      addLog(`File uploaded successfully to storage`, 'success');
      
      setStatus('complete');

    } catch (err) {
      console.error('Error:', sanitizeLogMessage(err.message));
      setError(sanitizeLogMessage(err.message));
      addLog(`Error occurred during upload`, 'error');
      setStatus('error');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          File Upload Test
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Test file uploads to {getCloudProvider().toUpperCase()}
        </Typography>

        {providerConfig && (
          <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Provider Configuration
            </Typography>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
              {JSON.stringify(sanitizeResponseData(providerConfig), null, 2)}
            </Typography>
          </Box>
        )}

        <Box sx={{ my: 3 }}>
          <Button
            variant="contained"
            onClick={runTest}
            disabled={status === 'running'}
            color="primary"
          >
            {status === 'running' ? 'Testing...' : 'Run Upload Test'}
          </Button>
        </Box>

        <Box sx={{ my: 2 }}>
          <Chip
            label={`Status: ${status}`}
            color={getStatusColor()}
            variant="outlined"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        <Card variant="outlined">
          <CardHeader 
            title="Test Logs"
            sx={{ bgcolor: 'grey.50' }}
          />
          <Divider />
          <CardContent sx={{ 
            height: 400, 
            overflow: 'auto',
            bgcolor: 'grey.50'
          }}>
            {logs.map((log, index) => (
              <Typography
                key={index}
                variant="body2"
                component="div"
                fontFamily="monospace"
                sx={{
                  mb: 1,
                  color: log.type === 'error' ? 'error.main' :
                         log.type === 'success' ? 'success.main' :
                         'text.primary'
                }}
              >
                {log.message}
              </Typography>
            ))}
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
};

export default FileUploadTest;